import React, { useState, useEffect, useMemo } from "react";
import {
  Search, BookOpen, MapPin, MessageSquare, Scroll, Eye, Users, Swords,
  Building2, Crown, Package, HelpCircle, ChevronDown, Sparkles, Target,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import type { WidgetAction } from "@/lib/mechanics/types";
import { useLocale } from "@/hooks/useLocale";

/* ── types ── */
interface LoreEntry {
  id: string;
  title: string;
  content: string;
  source: string;
  category?: string;
  discovered_turn: number;
  location?: string;
}

interface DiscoveredEntity {
  id: string;
  name: string;
  type: string;
  description: string;
  image_url: string | null;
  properties: any;
  visibility: string;
}

interface InventoryItemRef {
  name: string;
  description?: string;
  tags?: string[];
  quantity?: number;
  image_url?: string;
  scope?: string;
}

interface LoreCodexPanelProps {
  discovered_lore?: LoreEntry[];
  adventure_id?: string;
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
  /** Known items from inventory / game state */
  knownItems?: InventoryItemRef[];
  /** Known NPCs from active_npcs in state */
  knownNpcs?: { name: string; description?: string; image_url?: string; role?: string }[];
  /** Current location name */
  currentLocation?: string;
}

/* ── category config ── */
import type { Translations } from "@/lib/i18n/types";

interface CategoryDef {
  key: string;
  labelKey: keyof Translations;
  icon: React.ReactNode;
  entityTypes: string[];
  loreCategories: string[];
}

const CATEGORIES: CategoryDef[] = [
  { key: "characters", labelKey: "play_codex_characters", icon: <Users className="h-3.5 w-3.5" />, entityTypes: ["npc", "character", "creature"], loreCategories: [] },
  { key: "locations", labelKey: "play_codex_locations", icon: <MapPin className="h-3.5 w-3.5" />, entityTypes: ["location", "region", "place"], loreCategories: ["Geography"] },
  { key: "quests", labelKey: "play_codex_quests", icon: <Target className="h-3.5 w-3.5" />, entityTypes: ["quest"], loreCategories: ["Quest"] },
  { key: "factions", labelKey: "play_codex_factions", icon: <Crown className="h-3.5 w-3.5" />, entityTypes: ["faction", "organization", "guild", "group"], loreCategories: ["Faction", "Organization"] },
  { key: "items", labelKey: "play_codex_items", icon: <Package className="h-3.5 w-3.5" />, entityTypes: ["item", "artifact", "weapon", "armor", "equipment"], loreCategories: ["Item", "Artifact"] },
  { key: "lore", labelKey: "play_codex_lore", icon: <BookOpen className="h-3.5 w-3.5" />, entityTypes: ["concept", "event", "lore"], loreCategories: ["History", "Magic", "Culture", "Science", "Religion", "Legend", "Myth"] },
];

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  environmental: <Eye className="h-2.5 w-2.5" />,
  collectible: <Scroll className="h-2.5 w-2.5" />,
  conversational: <MessageSquare className="h-2.5 w-2.5" />,
  other: <BookOpen className="h-2.5 w-2.5" />,
};

interface CodexItem {
  id: string;
  name: string;
  description: string;
  image_url?: string | null;
  category: string;
  source?: string;
  location?: string;
  entityType?: string;
  isEntity: boolean;
}

export function LoreCodexPanel({ discovered_lore = [], adventure_id, knownItems = [], knownNpcs = [], currentLocation }: LoreCodexPanelProps) {
  const { t } = useLocale();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set(["characters", "locations", "items"]));
  const [dbLore, setDbLore] = useState<LoreEntry[]>([]);
  const [entities, setEntities] = useState<DiscoveredEntity[]>([]);

  // Fetch persisted lore + subscribe to changes
  const fetchLore = React.useCallback(() => {
    if (!adventure_id) return;
    supabase
      .from("adventure_lore")
      .select("lore_key, title, content, source, category, location, discovered_turn")
      .eq("adventure_id", adventure_id)
      .order("discovered_turn", { ascending: true })
      .then(({ data }) => {
        if (data) setDbLore(data.map((r: any) => ({
          id: r.lore_key, title: r.title, content: r.content, source: r.source,
          category: r.category, discovered_turn: r.discovered_turn, location: r.location,
        })));
      });
  }, [adventure_id]);

  const fetchEntities = React.useCallback(() => {
    if (!adventure_id) return;
    supabase
      .from("adventure_entities")
      .select("id, name, type, description, image_url, properties, visibility")
      .eq("adventure_id", adventure_id)
      .is("deleted_at", null)
      .in("visibility", ["visible", "revealed"])
      .then(({ data }) => {
        if (data) setEntities(data as DiscoveredEntity[]);
      });
  }, [adventure_id]);

  // Initial fetch
  useEffect(() => { fetchLore(); }, [fetchLore]);
  useEffect(() => { fetchEntities(); }, [fetchEntities]);

  // Realtime subscriptions — refresh on INSERT/UPDATE
  useEffect(() => {
    if (!adventure_id) return;
    const channel = supabase.channel(`codex-${adventure_id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "adventure_lore", filter: `adventure_id=eq.${adventure_id}` }, () => fetchLore())
      .on("postgres_changes", { event: "*", schema: "public", table: "adventure_entities", filter: `adventure_id=eq.${adventure_id}` }, () => fetchEntities())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [adventure_id, fetchLore, fetchEntities]);

  // Merge everything into categorized items
  const { items, totalCount } = useMemo(() => {
    const allItems: CodexItem[] = [];
    const seen = new Set<string>();

    // Entities → codex items
    for (const e of entities) {
      const key = `entity:${e.id}`;
      seen.add(e.name.toLowerCase());
      allItems.push({
        id: key,
        name: e.name,
        description: e.description,
        image_url: e.image_url,
        category: categorizationType(e.type),
        entityType: e.type,
        isEntity: true,
      });
    }

    // Known NPCs from game state (active_npcs) — only if not already an entity
    for (const npc of knownNpcs) {
      const nameKey = npc.name.toLowerCase();
      if (seen.has(nameKey)) continue;
      seen.add(nameKey);
      allItems.push({
        id: `npc:${nameKey}`,
        name: npc.name,
        description: npc.description || npc.role || "Known character",
        image_url: npc.image_url,
        category: "characters",
        entityType: npc.role || "npc",
        isEntity: false,
      });
    }

    // Known items from inventory — only if not already an entity
    for (const item of knownItems) {
      const nameKey = item.name.toLowerCase();
      if (seen.has(nameKey)) continue;
      seen.add(nameKey);
      const qty = item.quantity && item.quantity > 1 ? ` (×${item.quantity})` : "";
      allItems.push({
        id: `item:${nameKey}`,
        name: item.name + qty,
        description: item.description || (item.tags?.length ? item.tags.join(", ") : "Known item"),
        image_url: item.image_url,
        category: "items",
        entityType: "item",
        location: item.scope ? `${item.scope} inventory` : undefined,
        isEntity: false,
      });
    }

    // Current location as a codex entry
    if (currentLocation && !seen.has(currentLocation.toLowerCase())) {
      seen.add(currentLocation.toLowerCase());
      allItems.push({
        id: `loc:${currentLocation}`,
        name: currentLocation,
        description: "Current location",
        category: "locations",
        entityType: "location",
        isEntity: false,
      });
    }

    // Lore → codex items (dedup DB + state)
    const loreMap = new Map<string, LoreEntry>();
    for (const e of dbLore) loreMap.set(e.id, e);
    for (const e of discovered_lore) loreMap.set(e.id, e);
    for (const l of loreMap.values()) {
      allItems.push({
        id: `lore:${l.id}`,
        name: l.title,
        description: l.content,
        category: categorizeLore(l.category),
        source: l.source,
        location: l.location,
        isEntity: false,
      });
    }

    return { items: allItems, totalCount: allItems.length };
  }, [entities, dbLore, discovered_lore, knownItems, knownNpcs, currentLocation]);

  // Filter by search
  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i => i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
  }, [items, search]);

  // Group by category
  const grouped = useMemo(() => {
    const groups = new Map<string, CodexItem[]>();
    for (const cat of CATEGORIES) groups.set(cat.key, []);
    groups.set("other", []);
    for (const item of filtered) {
      const bucket = groups.get(item.category) || groups.get("other")!;
      bucket.push(item);
    }
    return groups;
  }, [filtered]);

  const toggleCategory = (key: string) => {
    setOpenCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  if (totalCount === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <Sparkles className="h-8 w-8 text-muted-foreground/50" />
        <div>
          <p className="text-sm font-medium text-muted-foreground">{t.play_codex_empty}</p>
          <p className="text-xs text-muted-foreground/70 mt-1 max-w-[220px]">
            {t.play_codex_empty_desc}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-1">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <BookOpen className="h-4 w-4 text-primary" />
        <span>{t.play_codex_title}</span>
        <Badge variant="secondary" className="ml-auto text-[10px]">{totalCount}</Badge>
      </div>

      {totalCount > 5 && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t.play_codex_search} className="h-7 pl-7 text-xs" />
        </div>
      )}

      <ScrollArea className="max-h-[500px]">
        <div className="space-y-1">
          {CATEGORIES.map(cat => {
            const catItems = grouped.get(cat.key) || [];
            if (catItems.length === 0) return null;
            const isOpen = openCategories.has(cat.key);

            return (
              <Collapsible key={cat.key} open={isOpen} onOpenChange={() => toggleCategory(cat.key)}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-accent/50 transition-colors">
                  <span className="text-primary">{cat.icon}</span>
                  <span className="text-xs font-semibold text-foreground flex-1 text-left">{t[cat.labelKey]}</span>
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0">{catItems.length}</Badge>
                  <ChevronDown className={cn("h-3 w-3 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-0.5 pl-2 mt-0.5">
                    {catItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                        className={cn(
                          "w-full text-left rounded-md border border-border/50 p-2 transition-colors hover:bg-accent/40",
                          expanded === item.id && "bg-accent/30 border-primary/30"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {item.image_url ? (
                            <Avatar className="h-6 w-6 shrink-0">
                              <AvatarImage src={item.image_url} alt={item.name} />
                              <AvatarFallback className="text-[9px] bg-accent">{item.name[0]}</AvatarFallback>
                            </Avatar>
                          ) : (
                            <span className="text-muted-foreground">{SOURCE_ICONS[item.source || "other"] || SOURCE_ICONS.other}</span>
                          )}
                          <span className="text-xs font-medium text-foreground truncate flex-1">{item.name}</span>
                          {item.entityType && (
                            <Badge variant="outline" className="text-[8px] px-1 py-0 capitalize">{item.entityType}</Badge>
                          )}
                        </div>
                        {expanded === item.id && (
                          <div className="mt-2 space-y-1 border-t border-border/30 pt-2">
                            <p className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed">{item.description}</p>
                            {item.location && (
                              <div className="flex items-center gap-1 text-[10px] text-muted-foreground/70">
                                <MapPin className="h-2.5 w-2.5" />
                                <span>{item.location}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}

          {/* Other/uncategorized */}
          {(() => {
            const otherItems = grouped.get("other") || [];
            if (otherItems.length === 0) return null;
            const isOpen = openCategories.has("other");
            return (
              <Collapsible open={isOpen} onOpenChange={() => toggleCategory("other")}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-accent/50 transition-colors">
                  <HelpCircle className="h-3.5 w-3.5 text-primary" />
                  <span className="text-xs font-semibold text-foreground flex-1 text-left">{t.play_codex_other}</span>
                  <Badge variant="outline" className="text-[9px] px-1.5 py-0">{otherItems.length}</Badge>
                  <ChevronDown className={cn("h-3 w-3 text-muted-foreground transition-transform", isOpen && "rotate-180")} />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="space-y-0.5 pl-2 mt-0.5">
                    {otherItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                        className={cn(
                          "w-full text-left rounded-md border border-border/50 p-2 transition-colors hover:bg-accent/40",
                          expanded === item.id && "bg-accent/30 border-primary/30"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{SOURCE_ICONS[item.source || "other"]}</span>
                          <span className="text-xs font-medium text-foreground truncate flex-1">{item.name}</span>
                        </div>
                        {expanded === item.id && (
                          <div className="mt-2 pt-2 border-t border-border/30">
                            <p className="text-xs text-muted-foreground whitespace-pre-wrap">{item.description}</p>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })()}
        </div>
      </ScrollArea>
    </div>
  );
}

/* ── helpers ── */
function categorizationType(entityType: string): string {
  const t = entityType.toLowerCase();
  for (const cat of CATEGORIES) {
    if (cat.entityTypes.includes(t)) return cat.key;
  }
  return "other";
}

function categorizeLore(category?: string): string {
  if (!category) return "lore";
  const c = category;
  for (const cat of CATEGORIES) {
    if (cat.loreCategories.some(lc => lc.toLowerCase() === c.toLowerCase())) return cat.key;
  }
  return "lore";
}
